import React, { useState, useEffect } from 'react';
import { Form, message } from 'antd';
import RecognitionSettingsPresentation from '../../presentation/RecognitionSettingsPresentation';
import { snappy } from 'src/_shared';
import { withApollo } from 'react-apollo';
import { gql, useQuery } from '@apollo/client';
import { Spinner } from 'src/_shared';
import { withGetCompany } from 'src/_shared/api/components/settings/';
import { connect } from 'react-redux';
import { userActions } from 'src/actions.js';

const CreateSnappyGrant = gql`mutation CreateSnappyGrant($input: SnappyGrantInput!) {
  createSnappyGrant(input: $input) {
    id
    name
    description
    externalSnappyId
    companyId
    snappyCampaignId
    snappyCampaignTierId
    active
    allowOverBudget
    allowMultipleGrants
    allowForNominations
    allowRevoke
    requireApproval
    allowAssignmentNoGift
    grantCategory
    costInCurrency
    currencyIsoCode
    currencySymbol
    giftExpiryDays
    remainingAssignments
    totalAssignments
    badgeIds
    badgeAssignmentIds
    snappyGiftIds
    isHidden
    isArchived
    isMirroredSnappyCampaignTier
    externalInformation
    redemptionLogs
    createdBy
    createdAt
  }
}`


const UpdateSnappyGrant = gql`mutation UpdateSnappyGrant($input: SnappyGrantInput!) {
  updateSnappyGrant(input: $input) {
    id
    name
    description
    externalSnappyId
    companyId
    snappyCampaignId
    snappyCampaignTierId
    active
    allowOverBudget
    allowMultipleGrants
    allowForNominations
    allowRevoke
    requireApproval
    allowAssignmentNoGift
    grantCategory
    costInCurrency
    currencyIsoCode
    currencySymbol
    giftExpiryDays
    remainingAssignments
    totalAssignments
    badgeIds
    badgeAssignmentIds
    snappyGiftIds
    isHidden
    isArchived
    isMirroredSnappyCampaignTier
    externalInformation
    redemptionLogs
    updatedBy
    updatedAt
  }
}`

const QuerySnappyGrantsByCompanyId = gql`query QuerySnappyGrantsByCompanyId(
  $companyId: ID!
  $limit: Int
  $nextToken: String
) {
  querySnappyGrantsByCompanyId(
    companyId: $companyId
    limit: $limit
    nextToken: $nextToken
  ) {
    items {
      id
      name
      description
      externalSnappyId
      companyId
      snappyCampaignId
      snappyCampaignTierId
      active
      allowOverBudget
      allowMultipleGrants
      allowForNominations
      allowRevoke
      requireApproval
      allowAssignmentNoGift
      grantCategory
      costInCurrency
      currencyIsoCode
      currencySymbol
      giftExpiryDays
      remainingAssignments
      totalAssignments
      badgeIds
      badgeAssignmentIds
      snappyGiftIds
      isHidden
      isArchived
      isMirroredSnappyCampaignTier
      externalInformation
      redemptionLogs
      createdBy
      createdAt
      updatedBy
      updatedAt
    }
    nextToken
  }
}`

const QuerySnappyApiKeys = gql`query QuerySnappyApiKeys($companyId: ID!) {
  querySnappyApiKeys(companyId: $companyId) {
    createdAt
    companyId
    expirationDate
    enforceMtls
    id
    name
  }
}`

const GetSnappyAPIKey = gql`query GetSnappyAPIKey($companyId: ID!) {
  getSnappyAPIKey(companyId: $companyId) {
    createdAt
    keyId
    label
    last4
    status
    maskedKey
    lastVerifiedAt
  }
}`

function RecognitionSettingsContainer(props) {
	const { currentUser, onUpdateCompany, updateCurrentUserCompany } = props;
	const [campaigns, setCampaigns] = useState([]);
	const [snappyCampaigns, setSnappyCampaigns] = useState([]);
	const [formVisibleCampaignId, setFormVisibleCampaignId] = useState(null);
	const [showCreateCampaignForm, setShowCreateCampaignForm] = useState(false);
	const [inlineEditingIndex, setInlineEditingIndex] = useState(null);
	const [inlineFormData, setInlineFormData] = useState({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [deletingTierId, setDeletingTierId] = useState(null);
	const [updatingTierId, setUpdatingTierId] = useState(null);
	const [snappyGrants, setSnappyGrants] = useState([]);
	const [form] = Form.useForm();
	const [createCampaignForm] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [apiKeyStatus, setApiKeyStatus] = useState('checking');
	const [currentApiKey, setCurrentApiKey] = useState(null);

	const companyId = currentUser?.companyId || currentUser?.company?.id;

	const { data: apiKeyData, loading: apiKeyLoading, error: apiKeyError } = useQuery(GetSnappyAPIKey, {
		variables: { companyId },
		skip: !companyId,
		fetchPolicy: 'cache-and-network'
	});

	const { data: apiKeysData, loading: apiKeysLoading, error: apiKeysError } = useQuery(QuerySnappyApiKeys, {
		variables: { companyId },
		skip: !companyId,
		fetchPolicy: 'cache-and-network'
	});

	useEffect(() => {
		if (apiKeyData?.getSnappyAPIKey) {
			setCurrentApiKey(apiKeyData.getSnappyAPIKey);
		}
	}, [apiKeyData]);

	useEffect(() => {
		if (apiKeyLoading || apiKeysLoading) {
			setApiKeyStatus('checking');
		} else if (apiKeyError || apiKeysError) {
			setApiKeyStatus('inactive');
		} else if (apiKeysData?.querySnappyApiKeys && Array.isArray(apiKeysData.querySnappyApiKeys) && apiKeysData.querySnappyApiKeys?.[0]?.id) {
			setApiKeyStatus('active');
		} else {
			setApiKeyStatus('inactive');
		}
	}, [apiKeyLoading, apiKeysLoading, apiKeyError, apiKeysError, apiKeysData]);

	const syncOrphanedSnappyGrants = async () => {
		try {
			const currentCampaigns = campaigns.length > 0 ? campaigns : await getCampaigns();
			const currentSnappyGrants = snappyGrants.length > 0 ? snappyGrants : await querySnappyGrants();
			
			if (!currentCampaigns || !currentSnappyGrants) return;
			
			const existingTierIds = new Set();
			const existingGrantTierIds = new Set();
			
			currentCampaigns.forEach(campaign => {
				if (campaign.tiers) {
					campaign.tiers.forEach(tier => {
						existingTierIds.add(tier.id);
					});
				}
			});
			
			currentSnappyGrants.forEach(grant => {
				if (grant.snappyCampaignTierId) {
					existingGrantTierIds.add(grant.snappyCampaignTierId);
				}
			});
			const orphanedGrants = currentSnappyGrants.filter(grant =>
				grant.snappyCampaignTierId && !existingTierIds.has(grant.snappyCampaignTierId)
			);
			
			for (const grant of orphanedGrants) {
				const targetCampaign = currentCampaigns.find(campaign =>
					campaign.id === grant.snappyCampaignId ||
					campaign.campaignId === grant.snappyCampaignId
				);
				
				if (targetCampaign) {
					const oneTimeRewardMode = currentUser?.company?.recognitionSettings?.rewardsEarnedMechanism === "recognition";
					
					const tierPayload = {
						campaignId: targetCampaign.id,
						pointsRequired: oneTimeRewardMode ? 0 : grant.costInCurrency || 0,
						snappyCampaignId: grant.externalSnappyId || grant.snappyCampaignId,
						tierName: grant.name || 'Synced Tier',
						tierDescription: grant.description || 'Auto-synced from SnappyGrant',
						type: 'createCampaignTier',
						userId: currentUser.id,
					};
					
					try {
						const response = await snappy(tierPayload);
						console.log(`Created missing campaign tier for SnappyGrant ${grant.id}`);
					} catch (error) {
						console.error(`Failed to create campaign tier for SnappyGrant ${grant.id}:`, error);
					}
				}
			}
			console.log("existingTierIds", existingTierIds);
			console.log("existingGrantTierIds", existingGrantTierIds);
			
			const orphanedTiers = [];
			currentCampaigns.forEach(campaign => {
				if (campaign.tiers) {
					campaign.tiers.forEach(tier => {
						if (!existingGrantTierIds.has(tier.id)) {
							orphanedTiers.push({ ...tier, campaignId: campaign.id });
						}
					});
				}
			});
			
			for (const tier of orphanedTiers) {
				const company = currentUser.company;
				const companyId = currentUser.companyId || company?.id;
				const oneTimeRewardMode = currentUser?.company?.recognitionSettings?.rewardsEarnedMechanism === "recognition";
				
				const snappyGrantInput = {
					input: {
						externalSnappyId: tier.snappyCampaignId,
						snappyCampaignId: tier.campaignId,
						snappyCampaignTierId: tier.id,
						costInCurrency: oneTimeRewardMode ? tier.pointsRequired : (tier.pointsRequired || 0),
						name: tier.tierName,
						description: tier.tierDescription,
						companyId: companyId,
						active: true,
						
						allowOverBudget: company?.recognitionSettings?.allowAdminBalanceOverrides || false,
						allowMultipleGrants: true,
						allowForNominations: true,
						allowRevoke: true,
						requireApproval: company?.recognitionSettings?.requireAdminApproval || false,
						allowAssignmentNoGift: true,
						grantCategory: 'Default',
						currencyIsoCode: 'USD',
						currencySymbol: '$',
						giftExpiryDays: -1,
						remainingAssignments: -1,
						totalAssignments: 0,
						badgeIds: [],
						badgeAssignmentIds: [],
						snappyGiftIds: [],
						isHidden: false,
						isArchived: false,
						isMirroredSnappyCampaignTier: true,
						externalInformation: '"{}"',
						redemptionLogs: '"{}"',
						createdBy: currentUser.id
					}
				};
				
				try {
					await createSnappyGrant(snappyGrantInput);
					console.log(`Created missing SnappyGrant for campaign tier ${tier.id}`);
				} catch (error) {
					console.error(`Failed to create SnappyGrant for campaign tier ${tier.id}:`, error);
				}
			}
			
			if (orphanedGrants.length > 0 || orphanedTiers.length > 0) {
				await getCampaigns();
				await querySnappyGrants();
			}
		} catch (error) {
			console.error('Error syncing orphaned SnappyGrants:', error);
		}
	};

	const syncPointsRequiredWithRewardMode = async () => {
		try {
			const oneTimeRewardMode = currentUser?.company?.recognitionSettings?.rewardsEarnedMechanism === "recognition";
			const currentCampaigns = campaigns.length > 0 ? campaigns : await getCampaigns();
			const currentSnappyGrants = snappyGrants.length > 0 ? snappyGrants : await querySnappyGrants();
			
			if (!currentCampaigns || !currentSnappyGrants) return;
			
			let needsUpdate = false;
			
			for (const campaign of currentCampaigns) {
				if (campaign.tiers) {
					for (const tier of campaign.tiers) {
						const correspondingGrant = currentSnappyGrants.find(grant => grant.snappyCampaignTierId === tier.id);
						
						if (oneTimeRewardMode) {
							if (tier.pointsRequired !== 0) {
								console.log(`Updating tier ${tier.id} pointsRequired from ${tier.pointsRequired} to 0 (one-time mode)`);
								
								const payload = {
									id: tier.id,
									campaignId: campaign.id,
									pointsRequired: 1,
									snappyCampaignId: tier.snappyCampaignId,
									tierName: tier.tierName,
									tierDescription: tier.tierDescription,
									type: 'updateCampaignTier',
									userId: currentUser.id,
								};
								
								try {
									await snappy(payload);
									needsUpdate = true;
								} catch (error) {
									console.error(`Failed to update tier ${tier.id}:`, error);
								}
							}
						} else {
							if (correspondingGrant && tier.pointsRequired !== correspondingGrant.costInCurrency) {
								console.log(`Updating tier ${tier.id} pointsRequired from ${tier.pointsRequired} to ${correspondingGrant.costInCurrency} (milestone mode)`);
								
								const payload = {
									id: tier.id,
									campaignId: campaign.id,
									pointsRequired: correspondingGrant.costInCurrency,
									snappyCampaignId: tier.snappyCampaignId,
									tierName: tier.tierName,
									tierDescription: tier.tierDescription,
									type: 'updateCampaignTier',
									userId: currentUser.id,
								};
								
								try {
									await snappy(payload);
									needsUpdate = true;
								} catch (error) {
									console.error(`Failed to update tier ${tier.id}:`, error);
								}
							}
						}
					}
				}
			}
			
			if (needsUpdate) {
				await getCampaigns();
			}
		} catch (error) {
			console.error('Error syncing pointsRequired with reward mode:', error);
		}
	};

	useEffect(() => {
		const initializeData = async () => {
			if (!currentUser?.companyId) return;			
			const campaignsData = await getCampaigns();
			await getSnappyCampaigns();
			const grantsData = await querySnappyGrants();
			
			console.log('Data loaded, campaigns:', campaignsData);
			console.log('Data loaded, grants:', grantsData);
			
			await syncOrphanedSnappyGrants();
			await syncPointsRequiredWithRewardMode();
		};
		
		initializeData();
	}, [currentUser?.companyId]);

	const querySnappyGrants = async () => {
		const companyId = currentUser?.companyId || currentUser?.company?.id;
		try {
			const { data, response } = await props.client.query({
				query: QuerySnappyGrantsByCompanyId,
				variables: {
					companyId: companyId,
					limit: 1000,
					nextToken: null
				}
			});
			if (data?.querySnappyGrantsByCompanyId?.items) {
				setSnappyGrants(data.querySnappyGrantsByCompanyId.items);
				return data.querySnappyGrantsByCompanyId.items;
			}
			return [];
		} catch (e) {
			console.log(e);
			return [];
		}
	}

	const createSnappyGrant = async (input) => {
		try {
			const { data, response } = await props.client.mutate({
				mutation: CreateSnappyGrant,
				variables: input
			});
			return data?.createSnappyGrant;
		} catch (e) {
			console.error("Error creating snappy grant:", e);
			return null;
		}
	};

	const updateSnappyGrant = async (input) => {
		try {
			const { data, response } = props.client.mutate({
				mutation: UpdateSnappyGrant,
				variables: {
					...input,
				}
			});
		} catch (e) {
			console.log(e);
		}
	};

	const getCampaigns = async () => {
		try {
			setLoading(true);
			const response = await snappy({
				type: 'getCampaigns',
				userId: currentUser.id,
			});
			setCampaigns(response || []);
			setLoading(false);
			return response || [];
		} catch (error) {
			console.error('Error fetching campaigns:', error);
			setLoading(false);
			return [];
		}
	};

	const getSnappyCampaigns = async () => {
		try {
			const response = await snappy({
				type: 'getSnappyCampaigns',
				userId: currentUser.id,
			});
			setSnappyCampaigns(response || []);
		} catch (error) {
			console.error('Error fetching snappyCampaigns:', error);
		}
	};

	const onCreateCampaign = async (values) => {
		setIsSubmitting(true);
		try {
			const payload = {
				type: 'createCampaign',
				userId: currentUser.id,
				campaignName: values.campaignName,
				snappyAPIKey: values.snappyAPIKey,
			};

			const response = await snappy(payload);

			setCampaigns([{ ...payload, id: response.id }]);

			message.success('Campaign created successfully!');
			setShowCreateCampaignForm(false);
			createCampaignForm.resetFields();
		} catch (error) {
			console.error('Error creating campaign:', error);
			message.error('Failed to create campaign.');
		} finally {
			setIsSubmitting(false);
		}
	};

	const onFinish = async (values, campaignId) => {
		setIsSubmitting(true);
		const oneTimeRewardMode = currentUser?.company?.recognitionSettings?.rewardsEarnedMechanism === "recognition";
		
		try {
			const originalPointsRequired = values.pointsRequired;
			
			const payload = {
				campaignId,
				pointsRequired: oneTimeRewardMode ? 0 : values.pointsRequired,
				snappyCampaignId: values.snappyCampaignId,
				tierName: values.tierName,
				tierDescription: values.tierDescription,
				type: 'createCampaignTier',
				userId: currentUser.id,
			};

			const response = await snappy(payload);
			const newTier = { ...payload, id: response.id };

			const company = currentUser.company;
			const companyId = currentUser.companyId || company?.id;
			const snappyGrantInput = {
				input: {
					externalSnappyId: values.snappyCampaignId,
					snappyCampaignId: campaignId,
					snappyCampaignTierId: response.id,
					costInCurrency: originalPointsRequired,
					name: values.tierName,
					description: values.tierDescription,
					companyId: companyId,
					active: true,
					
					allowOverBudget: company?.recognitionSettings?.allowAdminBalanceOverrides || false,
					allowMultipleGrants: true,
					allowForNominations: true,
					allowRevoke: true,
					requireApproval: company?.recognitionSettings?.requireAdminApproval || false,
					allowAssignmentNoGift: true,
					grantCategory: 'Default',
					currencyIsoCode: 'USD',
					currencySymbol: '$',
					giftExpiryDays: -1,
					remainingAssignments: -1,
					totalAssignments: 0,
					badgeIds: [],
					badgeAssignmentIds: [],
					snappyGiftIds: [],
					isHidden: false,
					isArchived: false,
					isMirroredSnappyCampaignTier: true,
					externalInformation: '"{}"',
					redemptionLogs: '"{}"',
					createdBy: currentUser.id
				}
			};

			await createSnappyGrant(snappyGrantInput);

			const updatedCampaigns = campaigns.map((campaign) =>
				campaign.id === campaignId
					? {
							...campaign,
							tiers: [...(campaign.tiers || []), newTier],
						}
					: campaign
			);

			setCampaigns(updatedCampaigns);
			
			await querySnappyGrants();

			setFormVisibleCampaignId(null);
			setIsSubmitting(false);
			message.success('Tier and corresponding Snappy Grant created successfully!');
		} catch (error) {
			console.error('Error creating campaign tier:', error);
			message.error('Failed to create tier and Snappy Grant.');
			setIsSubmitting(false);
		}
	};

	const onDelete = async (id, campaignId) => {
		setDeletingTierId(id);
		try {
			await snappy({
				type: 'deleteCampaignTier',
				userId: currentUser.id,
				id: id,
			});

			const updatedCampaigns = campaigns.map((campaign) =>
				campaign.id === campaignId
					? {
							...campaign,
							tiers: campaign.tiers.filter((tier) => tier.id !== id),
						}
					: campaign
			);
			setCampaigns(updatedCampaigns);

			message.success('Tier deleted successfully!');
		} catch (error) {
			console.error('Error deleting campaign tier:', error);
		} finally {
			setDeletingTierId(null);
		}
	};

	const onInlineEditSave = async (id, updatedTier, campaignId) => {
		setUpdatingTierId(id);
		const oneTimeRewardMode = currentUser?.company?.recognitionSettings?.rewardsEarnedMechanism === "recognition";
		
		try {
			const originalPointsRequired = updatedTier.pointsRequired;
			
			const payload = {
				id,
				campaignId,
				pointsRequired: oneTimeRewardMode ? 0 : updatedTier.pointsRequired,
				snappyCampaignId: updatedTier.snappyCampaignId,
				tierName: updatedTier.tierName,
				tierDescription: updatedTier.tierDescription,
				type: 'updateCampaignTier',
				userId: currentUser.id,
			};

			await snappy(payload);

			const correspondingGrant = snappyGrants.find(grant => grant.snappyCampaignTierId === id);
			if (correspondingGrant) {
				const company = currentUser.company;
				const companyId = currentUser.companyId || company?.id;
				const snappyGrantUpdateInput = {
					input: {
						id: correspondingGrant.id,
						externalSnappyId: updatedTier.snappyCampaignId,
						snappyCampaignId: campaignId,
						snappyCampaignTierId: id,
						costInCurrency: originalPointsRequired,
						name: updatedTier.tierName,
						description: updatedTier.tierDescription,
						companyId: companyId,
						active: true,
						
						allowOverBudget: company?.recognitionSettings?.allowAdminBalanceOverrides || false,
						allowMultipleGrants: true,
						allowForNominations: true,
						allowRevoke: true,
						requireApproval: company?.recognitionSettings?.requireAdminApproval || false,
						allowAssignmentNoGift: true,
						grantCategory: 'Default',
						currencyIsoCode: 'USD',
						currencySymbol: '$',
						giftExpiryDays: -1,
						remainingAssignments: -1,
						totalAssignments: 0,
						badgeIds: [],
						badgeAssignmentIds: [],
						snappyGiftIds: [],
						isHidden: false,
						isArchived: false,
						isMirroredSnappyCampaignTier: true,
						externalInformation: '"{}"',
						redemptionLogs: '"{}"',
						updatedBy: currentUser.id
					}
				};

				await updateSnappyGrant(snappyGrantUpdateInput);
			}

			const updatedCampaigns = campaigns.map((campaign) =>
				campaign.id === campaignId
					? {
							...campaign,
							tiers: campaign.tiers.map((tier) =>
								tier.id === id ? { ...updatedTier, id } : tier
							),
						}
					: campaign
			);

			setCampaigns(updatedCampaigns);
			
			await querySnappyGrants();
			
			message.success('Tier and corresponding Snappy Grant updated successfully!');
			setInlineEditingIndex(null);
		} catch (error) {
			console.error('Error updating campaign tier:', error);
			message.error('Failed to update the tier and Snappy Grant.');
		} finally {
			setUpdatingTierId(null);
		}
	};

	const onInlineEditCancel = () => {
		setInlineEditingIndex(null);
	};

	const handleInlineInputChange = (key, value) => {
		setInlineFormData({
			...inlineFormData,
			[key]: value,
		});
	};

	const handleInlineEditClick = (id, tier) => {
		setInlineEditingIndex(id);
		setInlineFormData(tier);
	};

	const handleUpdateCompany = async (values) => {
		const previousMechanism = currentUser?.company?.recognitionSettings?.rewardsEarnedMechanism;
		const newMechanism = values.rewardsEarnedMechanism;
		const isChangingToRecognition = previousMechanism === 'milestone' && newMechanism === 'recognition';
		const isChangingToMilestone = previousMechanism === 'recognition' && newMechanism === 'milestone';
		const isAlreadyRecognitionWithNoCampaigns = newMechanism === 'recognition' && snappyGrants.length === 0;
		
		const result = await onUpdateCompany({
			input: {
				id: currentUser?.company?.id,
				recognitionSettings: {
					...values
				},
			}
		});
		try {
			if (result?.data?.updateCompany) {
				updateCurrentUserCompany(result?.data?.updateCompany);
				
				if (isChangingToRecognition || isChangingToMilestone) {
					await syncPointsRequiredWithRewardMode();
				}
					
				if ((isChangingToRecognition || isAlreadyRecognitionWithNoCampaigns) && campaigns.length > 0) {
					const currentSnappyGrants = await querySnappyGrants() || [];
					const mirroredTiers = [];
					currentSnappyGrants.forEach((grant) => {
						if (grant?.snappyCampaignTierId) {
							mirroredTiers.push(grant.snappyCampaignTierId);
						}
					});
					const createGrantPromises = campaigns.map(campaign => {
						if (!campaign.tiers || campaign.tiers.length === 0) return null;
						return Promise.all(campaign.tiers.map(tier => {
							const company = currentUser.company;
							const companyId = currentUser.companyId || company?.id;
							if (mirroredTiers.includes(tier.id)) {
								return;
							}
							const snappyGrantInput = {
								input: {
									snappyCampaignId: campaign.campaignId || campaign.id,
									snappyCampaignTierId: tier.id,
									costInCurrency: tier.pointsRequired,
									name: tier.tierName,
									description: tier.tierDescription,
									companyId: companyId,
									active: true,
									
									allowOverBudget: company?.recognitionSettings?.allowAdminBalanceOverrides || false,
									allowMultipleGrants: true,
									allowForNominations: true,
									allowRevoke: true,
									requireApproval: company?.recognitionSettings?.requireAdminApproval || false,
									allowAssignmentNoGift: true,
									grantCategory: 'Default',
									currencyIsoCode: 'USD',
									currencySymbol: '$',
									giftExpiryDays: -1,
									remainingAssignments: -1,
									totalAssignments: 0,
									badgeIds: [],
									badgeAssignmentIds: [],
									snappyGiftIds: [],
									isHidden: false,
									isArchived: false,
									isMirroredSnappyCampaignTier: true,
									externalInformation: '"{}"',
									redemptionLogs: '"{}"',
									createdBy: currentUser.id
									
									// id and externalSnappyId are intentionally left blank
								}
							};
							
							return createSnappyGrant(snappyGrantInput);
						}));
					});
					
					await Promise.all(createGrantPromises.filter(Boolean));
					await querySnappyGrants();
				}
				
				message.success('Company updated successfully!');
			}
		} catch (error) {
			message.error('Something went wrong, please try again later');
			console.log(error);
		}
	}

	if (loading) {
		return <Spinner />;
	}

	return (
		<RecognitionSettingsPresentation
			currentUser={currentUser}
			handleUpdateCompany={handleUpdateCompany}
			campaigns={campaigns}
			snappyCampaigns={snappyCampaigns}
			formVisibleCampaignId={formVisibleCampaignId}
			setFormVisibleCampaignId={setFormVisibleCampaignId}
			onFinish={onFinish}
			onDelete={onDelete}
			onCreateCampaign={onCreateCampaign}
			showCreateCampaignForm={showCreateCampaignForm}
			setShowCreateCampaignForm={setShowCreateCampaignForm}
			inlineEditingIndex={inlineEditingIndex}
			onInlineEditSave={onInlineEditSave}
			onInlineEditCancel={onInlineEditCancel}
			form={form}
			createCampaignForm={createCampaignForm}
			inlineFormData={inlineFormData}
			handleInlineInputChange={handleInlineInputChange}
			handleInlineEditClick={handleInlineEditClick}
			isSubmitting={isSubmitting}
			deletingTierId={deletingTierId}
			updatingTierId={updatingTierId}
			apiKeyStatus={apiKeyStatus}
			currentApiKey={currentApiKey}
		/>
	);
}

const mapStateToProperties = (state) => {
	return {
	};
};

const mapDispatchToProperties = (dispatch) => {
	return {
		updateCurrentUserCompany(company) {
			dispatch(userActions.updateUserCompany(company));
		}
	};
};

export default withGetCompany(connect(mapStateToProperties,mapDispatchToProperties)(withApollo(RecognitionSettingsContainer)));
